(() => {
  'use strict';
  const KEY = 'bexco.buildList.v2', OLD_KEY = 'bexco.buildList.v1', LIMIT = 20, FRESH_MS = 300000;
  const text = (value, max) => typeof value === 'string' ? value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').trim().slice(0, max) : '';
  const id = value => /^\d{1,20}$/.test(String(value || '')) ? String(value) : '';
  const quantity = value => Math.max(1, Math.min(99, Math.floor(Number(value) || 1)));
  const currency = value => /^[A-Z]{3}$/.test(value || '') ? value : '';
  const money = (cents, code) => {
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined, {style:'currency', currency:code, currencyDisplay:'code'}).format(cents / 100); }
    catch (_) { return (cents / 100).toFixed(2) + ' ' + code; }
  };
  const safeUrl = (value, image = false) => {
    try {
      const raw = text(value, 1500);
      if (!raw) return '';
      const url = new URL(raw, location.origin);
      if (url.username || url.password) return '';
      if (image) return url.protocol === 'https:' && !url.port && (url.hostname === 'cdn.shopify.com' ||
        (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'))) ? url.href : '';
      if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return '';
      return url.pathname.replace(/\/$/, '');
    } catch (_) { return ''; }
  };
  const lineKey = entry => entry.handle + ':' + (entry.variantId || 'idea');
  const cleanItem = entry => {
    if (!entry || typeof entry !== 'object') return null;
    const handle = text(entry.handle, 255), title = text(entry.title, 220), url = safeUrl(entry.url);
    if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle) || !title || !url || url.split('/').pop() !== handle) return null;
    const variantId = id(entry.variantId);
    return {handle, title, url, image:safeUrl(entry.image, true), variantId,
      options:variantId && Array.isArray(entry.options) ? entry.options.slice(0, 3).map(value => text(value, 100)).filter(Boolean) : [],
      quantity:quantity(entry.quantity),
      lastPrice:Number.isSafeInteger(entry.lastPrice) && entry.lastPrice >= 0 ? entry.lastPrice : null,
      lastCurrency:currency(entry.lastCurrency), lastLabel:text(entry.lastLabel || entry.price, 80)};
  };
  const cleanState = source => {
    const data = source && !Array.isArray(source) && source.version === 2 ? source : {items:Array.isArray(source) ? source : []};
    const project = data.project || {}, seen = new Set();
    const items = (Array.isArray(data.items) ? data.items : []).slice(0, LIMIT).map(cleanItem).filter(entry => {
      if (!entry || seen.has(lineKey(entry))) return false;
      seen.add(lineKey(entry)); return true;
    });
    return {version:2, project:{name:text(project.name, 100), vehicle:text(project.vehicle, 160), notes:text(project.notes, 2000)}, items};
  };
  const init = () => {
    const dialog = document.getElementById('BexcoBuildList');
    if (!dialog || dialog.dataset.ready) return;
    dialog.dataset.ready = 'true';
    const find = name => dialog.querySelector('[data-bexco-build-' + name + ']');
    const list = find('items'), empty = find('empty'), clear = find('clear'), privacy = find('privacy');
    const subtotal = find('subtotal'), estimate = find('estimate'), refreshButton = find('refresh');
    const outsideStatus = document.querySelector('[data-bexco-build-status]'), insideStatus = find('dialog-status');
    const fallback = find('copy-fallback'), exportBox = document.getElementById('BexcoBuildExport');
    const pendingOwners = new WeakSet();
    let state = cleanState(null), persistent = true, returnFocus = null, noticeTimer, syncFrame, modalSession = 0;
    let prices = new Map(), refreshing = false, requestVersion = 0, controller = null, expiryTimer;
    const rootUrl = () => {
      const root = text(window.Shopify?.routes?.root || dialog.dataset.root || '/', 40);
      return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(root) ? root : '/';
    };
    const productLink = entry => entry.url + (entry.variantId ? '?variant=' + entry.variantId : '');
    const announce = message => {
      clearTimeout(noticeTimer);
      outsideStatus.textContent = dialog.open ? '' : message;
      insideStatus.textContent = dialog.open ? message : '';
      noticeTimer = setTimeout(() => { outsideStatus.textContent = ''; }, 6500);
    };
    const syncPrivacy = () => {
      const message = persistent ? 'Saved only in this browser. No account needed. Up to 20 saved lines.' :
        'Browser storage is unavailable. Keep this page open or download your project; changes last for this page visit only.';
      if (privacy.textContent !== message) privacy.textContent = message;
    };
    const persist = () => {
      try { localStorage.setItem(KEY, JSON.stringify(state)); persistent = true; }
      catch (_) { persistent = false; }
      syncPrivacy();
    };
    const read = () => {
      try {
        const current = localStorage.getItem(KEY), raw = current === null ? localStorage.getItem(OLD_KEY) : current;
        const result = raw && raw.length <= 120000 ? cleanState(JSON.parse(raw)) : cleanState(null);
        persistent = true;
        return {result, migrated:current === null && !!raw};
      } catch (_) { return {result:cleanState(null), migrated:false}; }
    };
    // The native form is authoritative; never infer a selection from a card or URL.
    const selection = button => {
      if (!button.hasAttribute('data-bexco-product-save')) return null;
      const owner = button.closest('product-info');
      if (!owner || pendingOwners.has(owner)) return null;
      const form = owner.querySelector('#' + CSS.escape('product-form-' + button.dataset.section));
      const variantId = id(form?.querySelector('input[name="id"]')?.value);
      if (!variantId) return null;
      const picker = owner.querySelector('variant-selects');
      const source = picker ? picker.querySelector('[data-selected-variant]') : owner.querySelector('[data-bexco-variant-fallback]');
      try {
        const selected = JSON.parse(source?.textContent || 'null');
        if (!selected || id(selected.id) !== variantId) return null;
        const input = owner.querySelector('input[name="quantity"][form="' + CSS.escape(form.id) + '"]') || form.querySelector('input[name="quantity"]');
        return {variantId, options:Array.isArray(selected.options) ? selected.options : [selected.title], quantity:quantity(input?.value)};
      } catch (_) { return null; }
    };
    const syncButtons = () => {
      document.querySelectorAll('[data-bexco-build-count]').forEach(element => {
        const value = String(state.items.length);
        if (element.textContent !== value) element.textContent = value;
        element.hidden = !state.items.length;
      });
      document.querySelectorAll('[data-bexco-save][data-bexco-product-save]').forEach(button => {
        const selected = selection(button), key = selected && lineKey({handle:button.dataset.handle, ...selected});
        const saved = !!key && state.items.some(item => lineKey(item) === key);
        button.dataset.bexcoAdded = String(saved);
        button.removeAttribute('aria-pressed');
        const label = button.querySelector('[data-bexco-save-label]');
        const value = saved ? 'View in build list' : 'Add to build list';
        if (label && label.textContent !== value) label.textContent = value;
      });
      syncPrivacy();
    };
    const scheduleSync = () => {
      if (syncFrame) return;
      syncFrame = requestAnimationFrame(() => { syncFrame = null; syncButtons(); });
    };
    const node = (tag, className, value) => {
      const element = document.createElement(tag);
      element.className = className;
      if (value) element.textContent = value;
      return element;
    };
    const currentPrice = item => {
      const price = prices.get(lineKey(item));
      return item.variantId && price?.status === 'current' && price.root === rootUrl() && Date.now() - price.checkedAt < FRESH_MS ? price : null;
    };
    const total = () => {
      const checked = state.items.map(currentPrice), code = checked[0]?.currency;
      if (!state.items.length || refreshing || checked.some(price => !price || price.currency !== code)) return null;
      const amount = checked.reduce((sum, price, index) => sum + price.price * state.items[index].quantity, 0);
      return Number.isSafeInteger(amount) ? {amount, currency:code} : null;
    };
    const updatePrices = () => {
      list.querySelectorAll('[data-line-key]').forEach(row => {
        const item = state.items.find(entry => lineKey(entry) === row.dataset.lineKey);
        if (!item) return;
        const price = currentPrice(item), known = prices.get(lineKey(item));
        let label;
        if (!item.variantId) label = 'Choose options on the product page to include this idea in an estimate.';
        else if (refreshing) label = 'Checking current price…';
        else if (price) label = money(price.price, price.currency) + ' each · ' + money(price.price * item.quantity, price.currency) + ' for this line' + (price.available ? '' : ' · Currently unavailable');
        else {
          const previous = item.lastPrice !== null && item.lastCurrency ? money(item.lastPrice, item.lastCurrency) : item.lastLabel;
          label = (known?.status === 'missing' ? 'This option could not be found. Check the product page.' : 'Current price not verified.') + (previous ? ' Last known: ' + previous + ' each; excluded from estimate.' : ' Excluded from estimate.');
        }
        const target = row.querySelector('[data-line-price]');
        if (target.textContent !== label) target.textContent = label;
        const options = row.querySelector('[data-line-options]');
        const optionLabel = item.variantId ? (item.options.filter(value => value !== 'Default Title').join(' / ') || 'Standard option') : 'Choose options';
        if (options.textContent !== optionLabel) options.textContent = optionLabel;
      });
      estimate.hidden = !state.items.length;
      refreshButton.disabled = refreshing || !state.items.some(item => item.variantId);
      const sum = total();
      const invalid = [...list.querySelectorAll('input[type="number"]')].some(input => !input.validity.valid);
      const value = invalid ? 'Enter quantities from 1 to 99 to see an estimate.' : sum ? 'Estimated product subtotal: ' + money(sum.amount, sum.currency) + ' · prices checked within the last 5 minutes.' :
        refreshing ? 'Checking prices—no subtotal until every saved line is verified.' : 'Subtotal unavailable. Choose options for every idea and refresh any unverified prices.';
      if (subtotal.textContent !== value) subtotal.textContent = value;
    };
    const render = () => {
      list.replaceChildren();
      empty.hidden = !!state.items.length;
      list.hidden = clear.hidden = !state.items.length;
      state.items.forEach((item, index) => {
        const row = node('li', 'bexco-build__item'); row.dataset.lineKey = lineKey(item);
        const picture = node(item.image ? 'img' : 'span', 'bexco-build__image');
        if (item.image) {
          picture.src = item.image; picture.alt = ''; picture.loading = 'lazy'; picture.width = picture.height = 76;
          picture.referrerPolicy = 'no-referrer'; picture.addEventListener('error', () => { picture.hidden = true; }, {once:true});
        } else picture.setAttribute('aria-hidden', 'true');
        const info = node('div', 'bexco-build__info'), link = node('a', 'bexco-build__title', item.title);
        link.href = productLink(item); info.append(link);
        const options = node('p', 'bexco-build__options'); options.dataset.lineOptions = '';
        const price = node('p', 'bexco-build__price'); price.dataset.linePrice = ''; info.append(options, price);
        const actions = node('div', 'bexco-build__line-actions');
        const label = node('label', 'bexco-build__quantity', 'Quantity');
        const input = node('input', ''); input.type = 'number'; input.min = '1'; input.max = '99'; input.step = '1'; input.required = true;
        input.inputMode = 'numeric'; input.value = String(item.quantity); input.dataset.bexcoQuantity = lineKey(item);
        input.id = 'BexcoBuildQuantity-' + index; label.htmlFor = input.id; input.setAttribute('aria-label', 'Quantity for ' + item.title + ' ' + (item.options.join(' / ') || 'product idea')); label.append(input);
        const choose = node('a', 'bexco-build__choose', item.variantId ? 'Review options' : 'Choose options'); choose.href = productLink(item);
        const remove = node('button', 'bexco-build__remove', 'Remove'); remove.type = 'button'; remove.dataset.bexcoBuildRemove = lineKey(item);
        remove.setAttribute('aria-label', 'Remove ' + item.title + ' ' + item.options.join(' / ') + ' from your build list');
        actions.append(label, choose, remove); info.append(actions); row.append(picture, info); list.append(row);
      });
      syncButtons(); updatePrices();
    };
    const cancelRefresh = () => { requestVersion++; controller?.abort(); controller = null; refreshing = false; clearTimeout(expiryTimer); };
    const refresh = async () => {
      cancelRefresh(); prices = new Map();
      const selected = state.items.filter(item => item.variantId);
      if (!selected.length) { updatePrices(); return; }
      refreshing = true; updatePrices();
      const generation = requestVersion, root = rootUrl();
      const abort = new AbortController(); controller = abort;
      const timeout = setTimeout(() => abort.abort(), 20000);
      let limited = false;
      const get = async path => {
        const response = await fetch(root + path, {method:'GET', credentials:'same-origin', cache:'no-store', redirect:'error', signal:abort.signal, headers:{Accept:'application/json'}});
        if (response.status === 429) limited = true;
        if (!response.ok) throw new Error('Public price check failed');
        const body = await response.text();
        if (body.length > 2000000) throw new Error('Response too large');
        return JSON.parse(body);
      };
      try {
        const code = currency((await get('cart.js')).currency);
        if (!code) throw new Error('Currency unavailable');
        const handles = [...new Set(selected.map(item => item.handle))], checked = new Map();
        let next = 0;
        await Promise.all(Array.from({length:Math.min(3, handles.length)}, async () => {
          while (next < handles.length && !limited && !abort.signal.aborted) {
            const handle = handles[next++];
            try {
              const product = await get('products/' + encodeURIComponent(handle) + '.js');
              if (product.handle !== handle || !Array.isArray(product.variants)) throw new Error('Product mismatch');
              selected.filter(item => item.handle === handle).forEach(item => {
                const variant = product.variants.find(value => id(value.id) === item.variantId);
                if (!variant) { checked.set(lineKey(item), {status:'missing'}); return; }
                if (!Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.available !== 'boolean') return;
                checked.set(lineKey(item), {status:'current', price:variant.price, currency:code, available:variant.available,
                  options:Array.isArray(variant.options) ? variant.options.slice(0, 3).map(value => text(value, 100)) : [text(variant.title, 100)], root, checkedAt:Date.now()});
              });
            } catch (_) { /* A failed product stays unverified; never turn a missing price into zero. */ }
          }
        }));
        if (limited || currency((await get('cart.js')).currency) !== code || root !== rootUrl()) throw new Error('Currency or request context changed');
        if (generation !== requestVersion) return;
        prices = checked;
        state.items.forEach(item => {
          const price = currentPrice(item);
          if (price) { item.lastPrice = price.price; item.lastCurrency = price.currency; item.lastLabel = ''; item.options = price.options; }
        });
        persist();
      } catch (_) {
        if (generation === requestVersion) prices = new Map();
      } finally {
        clearTimeout(timeout);
        if (generation === requestVersion) {
          refreshing = false; controller = null; updatePrices();
          announce(total() ? 'Current product prices checked. Shipping and tax are not included.' : 'Some prices or options could not be verified. Your saved list is still here; no complete subtotal is shown.');
          const deadlines = [...prices.values()].filter(price => price.status === 'current').map(price => price.checkedAt + FRESH_MS);
          if (deadlines.length) expiryTimer = setTimeout(updatePrices, Math.max(0, Math.min(...deadlines) - Date.now()) + 50);
        }
      }
    };
    const syncProject = () => dialog.querySelectorAll('[data-bexco-project]').forEach(input => { input.value = state.project[input.dataset.bexcoProject] || ''; });
    const focusReturnTarget = trigger => {
      const visible = element => {
        if (!element?.isConnected || element.disabled || element.closest('[hidden],[inert]') || !element.getClientRects().length) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
      };
      const reachable = element => {
        if (!element?.isConnected) return null;
        let target = element;
        // Opening the planner closes the mobile disclosure. Its summary stays
        // reachable even though the original menu item no longer is.
        for (let closed = element.closest('details:not([open])'); closed; closed = closed.parentElement?.closest('details:not([open])')) {
          target = closed.querySelector(':scope > summary');
        }
        return visible(target) ? target : null;
      };
      const openers = [...document.querySelectorAll('[data-bexco-build-open]')];
      return reachable(trigger) || openers.find(visible) || openers.map(reachable).find(Boolean) || null;
    };
    const close = () => {
      modalSession++;
      cancelRefresh(); updatePrices();
      if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
      document.documentElement.classList.remove('bexco-build-is-open');
      focusReturnTarget(returnFocus)?.focus();
    };
    const open = trigger => {
      modalSession++;
      returnFocus = trigger; syncProject(); render(); fallback.hidden = true; insideStatus.textContent = outsideStatus.textContent = '';
      if (!dialog.open) {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else { dialog.setAttribute('open', ''); dialog.dataset.fallback = ''; dialog.setAttribute('role', 'dialog'); dialog.setAttribute('aria-modal', 'true'); }
      }
      document.documentElement.classList.add('bexco-build-is-open'); find('close').focus(); refresh();
    };
    const normalizeQuantity = input => {
      const item = state.items.find(entry => lineKey(entry) === input.dataset.bexcoQuantity);
      if (!item) return;
      item.quantity = quantity(input.value); input.value = String(item.quantity); persist(); updatePrices();
    };
    const exportText = () => {
      list.querySelectorAll('[data-bexco-quantity]').forEach(normalizeQuantity);
      const lines = ['BEXCO BUILD LIST', 'Project: ' + (state.project.name || 'Untitled project')];
      if (state.project.vehicle) lines.push('Vehicle: ' + state.project.vehicle);
      if (state.project.notes) lines.push('', 'Project notes:', state.project.notes);
      lines.push('');
      state.items.forEach((item, index) => {
        const price = currentPrice(item);
        lines.push((index + 1) + '. ' + item.title, 'Options: ' + (item.variantId ? item.options.filter(value => value !== 'Default Title').join(' / ') || 'Standard option' : 'CHOOSE OPTIONS — not included in estimate'), 'Quantity: ' + item.quantity,
          'Product: ' + new URL(productLink(item), location.origin).href);
        lines.push(price ? 'Price checked: ' + money(price.price, price.currency) + ' each; line estimate ' + money(price.price * item.quantity, price.currency) + (price.available ? '' : ' (currently unavailable)') : 'Current price unverified; no line estimate.');
        lines.push('');
      });
      const sum = total();
      lines.push(sum ? 'Estimated product subtotal: ' + money(sum.amount, sum.currency) : 'Subtotal unavailable: every line needs chosen options and a verified current price.',
        'Shipping and tax are excluded. Prices and availability may change. No fitment guarantee or inventory reservation.',
        'This is a planning list, not a cart, quote, or order. Nothing has been emailed or sent automatically.');
      return lines.join('\n');
    };
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-bexco-build-open]');
      if (trigger) { event.preventDefault(); open(trigger); return; }
      const button = event.target.closest('[data-bexco-save]');
      if (!button || !button.hasAttribute('data-bexco-product-save') || button.disabled) return;
      event.preventDefault();
      const selected = selection(button);
      if (!selected) { announce('Let the product options finish updating, then add your selected options.'); return; }
      const item = cleanItem({...button.dataset, ...selected});
      if (!item) { announce('This product could not be saved. Please open its product page.'); return; }
      const existing = state.items.findIndex(entry => lineKey(entry) === lineKey(item));
      if (existing >= 0) { open(button); return; }
      const idea = item.variantId ? state.items.findIndex(entry => entry.handle === item.handle && !entry.variantId) : -1;
      if (idea < 0 && state.items.length >= LIMIT) { announce('Your build list holds 20 saved lines. Remove a line inside the planner to add another.'); return; }
      if (idea >= 0) { item.quantity = state.items[idea].quantity; state.items.splice(idea, 1, item); }
      else state.items.push(item);
      prices.delete(lineKey(item)); persist(); render();
      announce(item.title + ' added to your build list.' + (persistent ? '' : ' Download your project before leaving; browser storage is unavailable.'));
    });
    document.addEventListener('change', event => {
      if (!(event.target instanceof Element)) return;
      const owner = event.target.closest('product-info');
      if (owner && event.target.closest('variant-selects')) pendingOwners.add(owner);
      else if (owner && event.target.matches('input[name="id"]')) pendingOwners.delete(owner);
      scheduleSync();
    }, true);
    dialog.addEventListener('input', event => {
      const input = event.target;
      if (input.dataset.bexcoProject) { state.project[input.dataset.bexcoProject] = text(input.value, input.maxLength); persist(); }
      else if (input.dataset.bexcoQuantity) {
        if (input.validity.valid) { const item = state.items.find(entry => lineKey(entry) === input.dataset.bexcoQuantity); if (item) { item.quantity = quantity(input.value); persist(); } }
        updatePrices();
      }
    });
    dialog.addEventListener('change', event => { if (event.target.dataset.bexcoQuantity) normalizeQuantity(event.target); });
    dialog.addEventListener('click', async event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('button');
      if (button?.hasAttribute('data-bexco-build-close')) close();
      else if (button?.hasAttribute('data-bexco-build-refresh')) refresh();
      else if (button?.hasAttribute('data-bexco-build-clear')) {
        cancelRefresh(); prices.clear(); state.items = []; persist(); render(); find('close').focus(); announce('Saved products cleared. Your project details and notes remain.');
      } else if (button?.dataset.bexcoBuildRemove) {
        const index = state.items.findIndex(item => lineKey(item) === button.dataset.bexcoBuildRemove);
        if (index < 0) return;
        const removed = state.items.splice(index, 1)[0]; prices.delete(lineKey(removed)); persist(); render();
        const buttons = list.querySelectorAll('[data-bexco-build-remove]'); (buttons[Math.min(index, buttons.length - 1)] || find('close')).focus(); announce(removed.title + ' removed.');
      } else if (button?.hasAttribute('data-bexco-build-copy')) {
        const value = exportText(), session = modalSession;
        try { if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable'); await navigator.clipboard.writeText(value); if (session === modalSession && dialog.open) announce('Project copied. Nothing was sent.'); }
        catch (_) { if (session !== modalSession || !dialog.open) return; fallback.hidden = false; exportBox.value = value; exportBox.focus(); exportBox.select(); announce('Select and copy the project text below.'); }
      } else if (button?.hasAttribute('data-bexco-build-download')) {
        const blobUrl = URL.createObjectURL(new Blob([exportText()], {type:'text/plain;charset=utf-8'}));
        const anchor = document.createElement('a'); anchor.href = blobUrl; anchor.download = 'bexco-build-list.txt'; document.body.append(anchor); anchor.click(); anchor.remove(); setTimeout(() => URL.revokeObjectURL(blobUrl), 1000); announce('Project text downloaded for your own use.');
      } else if (event.target === dialog) {
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      }
    });
    dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex="0"]')].filter(element => element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (first && event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (last && !event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    window.addEventListener('storage', event => {
      if (event.key !== KEY && event.key !== null) return;
      cancelRefresh(); prices.clear(); state = event.newValue === null ? cleanState(null) : read().result; syncProject(); render();
      if (dialog.open) { find('close').focus(); announce('Your project was updated in another tab.'); refresh(); }
    });
    window.addEventListener('offline', () => { cancelRefresh(); prices.clear(); updatePrices(); if (dialog.open) announce('You are offline. Your project remains available; current prices cannot be verified.'); });
    document.addEventListener('shopify:section:load', scheduleSync);
    document.addEventListener('product-info:loaded', scheduleSync);
    new MutationObserver(scheduleSync).observe(document.body, {childList:true, subtree:true});
    const loaded = read(); state = loaded.result;
    if (loaded.migrated) persist();
    else { try { localStorage.getItem(KEY); } catch (_) { persistent = false; } }
    syncProject(); render();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
